import React from 'react';
import { Badge } from '../ui/badge';
import { formatDT, formatTimeAgo } from '../../utils/timezone';
import { fmtCoords, tripLocMapsUrl } from '../../utils/tripExport';
import { Battery, BatteryLow, BatteryWarning, Bluetooth, Link2, MapPin, Unlink } from 'lucide-react';

const batteryMeta = (health) => {
  const pct = health?.battery_percent;
  const tilde = health?.battery_estimated ? '~' : '';
  const title = health?.battery_mv != null ? `${health.battery_mv} mV${health?.battery_estimated ? ' (estimated from voltage)' : ''}` : undefined;
  if (pct == null) {
    if (health?.low_battery) return { label: 'Low', title, cls: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400', Icon: BatteryLow };
    return { label: '—', title, cls: 'text-muted-foreground', Icon: Battery };
  }
  if (health.low_battery || pct <= 20) return { label: `${tilde}${pct}%`, title, cls: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400', Icon: BatteryLow };
  if (pct <= 40) return { label: `${tilde}${pct}%`, title, cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400', Icon: BatteryWarning };
  return { label: `${tilde}${pct}%`, title, cls: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400', Icon: Battery };
};

const LocLink = ({ loc, testId }) => {
  const url = tripLocMapsUrl(loc);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-0.5 text-[#2563eb] hover:underline" data-testid={testId} onClick={(e) => e.stopPropagation()}>
      <MapPin className="w-3 h-3 shrink-0" /> {fmtCoords(loc)}
    </a>
  );
};

const BindingStatus = ({ binding, deviceId }) => {
  if (binding?.status === 'attached') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-green-100 px-1.5 py-0.5 text-[10px] font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300" data-testid={`asset-health-attached-${deviceId}`}>
        <Link2 className="w-3 h-3" /> Attached{binding.vehicle_name ? ` to ${binding.vehicle_name}` : ''}
        {binding.since && <span className="font-normal opacity-80" title={formatDT(binding.since)}>· {formatTimeAgo(binding.since)}</span>}
      </span>
    );
  }
  if (binding?.status === 'dropped') {
    return (
      <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300" data-testid={`asset-health-dropped-${deviceId}`}>
        <Unlink className="w-3 h-3" /> Dropped{binding.vehicle_name ? ` from ${binding.vehicle_name}` : ''}
        {binding.ended_at && <span className="font-normal opacity-80" title={formatDT(binding.ended_at)}>· {formatTimeAgo(binding.ended_at)}</span>}
      </span>
    );
  }
  return (
    <span className="text-[10px] text-muted-foreground" data-testid={`asset-health-neverbound-${deviceId}`}>Not attached yet</span>
  );
};

// Full block — used on the Trailers tab cards.
export const AssetBeaconHealthBlock = ({ health, deviceId }) => {
  if (!health) return null;
  const { label, cls, Icon, title } = batteryMeta(health);
  return (
    <div className="space-y-1.5 rounded-md border border-teal-200/60 bg-teal-50/40 p-2 dark:border-teal-800/50 dark:bg-teal-950/20" data-testid={`asset-beacon-health-${deviceId}`}>
      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground flex items-center gap-1"><Bluetooth className="w-3 h-3 text-teal-600" /> Beacon Health</span>
        <Badge variant="outline" title={title} className={`text-[10px] gap-1 ${cls}`} data-testid={`asset-health-battery-${deviceId}`}>
          <Icon className="w-3 h-3" /> {label}
        </Badge>
      </div>
      <div className="flex items-start justify-between gap-2 text-xs">
        <span className="text-muted-foreground shrink-0">Last seen</span>
        <span className="text-right" data-testid={`asset-health-lastseen-${deviceId}`}>
          {health.last_seen_at ? (
            <>
              <span title={formatDT(health.last_seen_at, { seconds: true })}>{formatTimeAgo(health.last_seen_at)}</span>
              {health.last_receiver_name && <span className="text-muted-foreground"> by {health.last_receiver_name}</span>}
            </>
          ) : <span className="text-muted-foreground">Never detected</span>}
        </span>
      </div>
      {health.last_location && (
        <div className="flex items-start justify-between gap-2 text-xs">
          <span className="text-muted-foreground shrink-0">Location</span>
          <LocLink loc={health.last_location} testId={`asset-health-location-${deviceId}`} />
        </div>
      )}
      <div className="flex items-start justify-between gap-2 text-xs">
        <span className="text-muted-foreground shrink-0">Status</span>
        <span className="text-right"><BindingStatus binding={health.binding} deviceId={deviceId} /></span>
      </div>
      {health.binding?.status === 'dropped' && health.binding.drop_location && (
        <div className="flex items-start justify-between gap-2 text-xs">
          <span className="text-muted-foreground shrink-0">Drop location</span>
          <LocLink loc={health.binding.drop_location} testId={`asset-health-droplocation-${deviceId}`} />
        </div>
      )}
    </div>
  );
};

// Compact cell — used in the Equipment table.
export const AssetBeaconHealthCompact = ({ health, deviceId }) => {
  if (!health) return <span className="text-xs text-muted-foreground">—</span>;
  const { label, cls, Icon, title } = batteryMeta(health);
  return (
    <div className="space-y-0.5" data-testid={`asset-beacon-health-compact-${deviceId}`}>
      <div className="flex items-center gap-1.5">
        <Badge variant="outline" title={title} className={`text-[10px] gap-1 ${cls}`} data-testid={`asset-health-battery-${deviceId}`}>
          <Icon className="w-3 h-3" /> {label}
        </Badge>
        <span className="text-[10px] text-muted-foreground" data-testid={`asset-health-lastseen-${deviceId}`} title={health.last_seen_at ? formatDT(health.last_seen_at, { seconds: true }) : undefined}>
          {health.last_seen_at ? formatTimeAgo(health.last_seen_at) : 'Never detected'}
        </span>
      </div>
      <div className="flex items-center gap-1.5">
        <BindingStatus binding={health.binding} deviceId={deviceId} />
        {health.binding?.status === 'dropped' && health.binding.drop_location && (
          <LocLink loc={health.binding.drop_location} testId={`asset-health-droplocation-${deviceId}`} />
        )}
      </div>
    </div>
  );
};
